const moment = require('moment');
const {
    addTermToDate2,
    getYearFromDate,
    parseTenor
} = require('./irs.helper.service');
const parsePlotModule = require('../lib/getFormattedLegs');

const parsePlot = parsePlotModule.default || parsePlotModule;

const CUSTOM_DATE_INPUT_FORMATS = ['DD/MM/YY', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601];

const toIsoLegDate = (raw) => {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;
    const parsed = moment.utc(trimmed, CUSTOM_DATE_INPUT_FORMATS, true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

// legArr -> []String
// returns -> []String
const replaceLegsWithCustomDates = (legArr, customLabelsCsv) => {
    if (!legArr || !customLabelsCsv) return legArr;
    if (typeof legArr === 'string') legArr = legArr.split(',');
    const labels = String(customLabelsCsv).split(',');
    const tokens = legArr.map((s) => s.trim()).filter(Boolean);
    let mutated = false;
    const next = tokens.map((tok, idx) => {
        const iso = toIsoLegDate(labels[idx]);
        if (!iso) return tok;
        mutated = true;
        return iso;
    });
    return mutated ? next : legArr;
};

const ZERO_Y_SPOT = /(?:^|\s)0y/gi;
const LEG_TOKEN = /^(\d+(?:\.\d+)?)([dwmy])$/i;
const COMPACT_FWD_PAIR = /^(\d+(?:\.\d+)?)([DWMY])(\d+(?:\.\d+)?)([DWMY])$/i;
const STANDALONE_TENOR = /^\d+(?:\.\d+)?[DWMY]$/i;
const FULL_IMM_FWD_TOKEN = /^([MUZH]\d{1,2})(\d+(?:\.\d+)?)([DWMY])$/i;

const isImmLabelLeg = (leg) => /^[MUZH]\d+y$/i.test(String(leg ?? '').trim());

const extractImmLabel = (structureRaw = '') => {
    const matches = String(structureRaw)
        .toUpperCase()
        .match(/[MUZH]\d+Y/g);
    return matches?.length ? matches : [];
};

const findImmByLabel = (label, immList = []) => {
    if (!label) return null;
    const target = String(label).toLowerCase();
    return (
        immList.find((item) => item.label.toLowerCase() === target)
        || immList.find((item) => item.oldLabel?.toLowerCase() === target)
    );
};

const buildImmYearLabelMap = (validImm = []) => {
    const map = new Map();
    validImm.forEach((imm) => {
        if (imm?.y != null) map.set(String(imm.y), imm.label?.toUpperCase() || '');
    });
    return map;
};

function normalizeLeg(leg) {
    return String(leg ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '')
        .replace(LEG_TOKEN, '$1$2');
}

function normalizeDisplayLegsFromInput(legsOrCsv) {
    if (Array.isArray(legsOrCsv)) {
        return legsOrCsv.map(normalizeLeg).filter(Boolean);
    }
    if (!legsOrCsv) return [];
    return String(legsOrCsv)
        .split(',')
        .map((l) => l.trim().replace(/\s+/g, '').toUpperCase())
        .filter(Boolean);
}

function checkPlotStructure(legCount, isFwd) {
    if (isFwd) {
        if (legCount === 6) return 'butterfly';
        if (legCount === 4) return 'spread';
        if (legCount === 3) return 'butterfly';
        if (legCount === 2) return 'spread';
    } else if (legCount === 3) {
        return 'butterfly';
    } else if (legCount === 2) {
        return 'spread';
    }
    return 'other';
}

function immLabelAsc(tokenCount, isFwd) {
    if (tokenCount < 2) return true;
    const kind = checkPlotStructure(tokenCount, isFwd);
    return kind !== 'spread';
}

function sortPairsAscending(legPairs) {
    const pairs = legPairs.map((x) => {
        const start = addTermToDate2(x[0]);
        return {
            pair: x,
            start: getYearFromDate(start),
            end: getYearFromDate(addTermToDate2(x[1], start))
        };
    });

    return pairs
        .toSorted(({ start: s0, end: e0 }, { start: s1, end: e1 }) => {
            if (e0 !== e1) return e0 - e1;
            return s0 - s1;
        })
        .map((x) => x.pair);
}

function sortForwardLegPairs(legPairs, ascending = true) {
    if (!legPairs?.length) return [];
    if (legPairs.length !== 2 && legPairs.length !== 3) return legPairs.flat();

    const sortedPairs = sortPairsAscending(legPairs);
    const orderedPairs = ascending ? sortedPairs : [...sortedPairs].reverse();
    return orderedPairs.flat();
}

function sortFlatForwardLegs(legs, ascending = true) {
    const normalized = normalizeDisplayLegsFromInput(legs);
    if (normalized.length < 4 || normalized.length % 2 !== 0) return normalized;

    const pairs = [];
    for (let i = 0; i < normalized.length; i += 2) {
        pairs.push([normalized[i], normalized[i + 1]]);
    }
    return sortForwardLegPairs(pairs, ascending);
}

function sortFlatForwardLegsAscending(legs) {
    return sortFlatForwardLegs(legs, true);
}

function sortSpotTenors(legs, ascending = true) {
    const normalized = normalizeDisplayLegsFromInput(legs);
    if (normalized.length < 2) return normalized;
    return [...normalized].sort((a, b) => (ascending ? parseTenor(a) - parseTenor(b) : parseTenor(b) - parseTenor(a)));
}

function sortSpotTenorsAscending(legs) {
    return sortSpotTenors(legs, true);
}

function sortDisplayLegs(legs, isFwd) {
    const normalized = normalizeDisplayLegsFromInput(legs);
    if (!normalized.length) return normalized;

    const kind = checkPlotStructure(normalized.length, isFwd);
    const ascending = kind !== 'spread';

    if (isFwd && normalized.length >= 4 && normalized.length % 2 === 0) {
        return sortFlatForwardLegs(legs, ascending);
    }
    if (!isFwd && normalized.length >= 2) {
        return sortSpotTenors(legs, ascending);
    }
    return normalized;
}

function sortLegsAsc(legs, isFwd) {
    return sortDisplayLegs(legs, isFwd);
}

function compactPairChunksFromFlatLegs(flatLegs) {
    const chunks = [];
    for (let i = 0; i < flatLegs.length; i += 2) {
        if (flatLegs[i + 1]) chunks.push(`${flatLegs[i]}${flatLegs[i + 1]}`);
    }
    return chunks;
}

function sortCompactPairTokens(tokens, ascending = true) {
    const pairs = [];
    const passthrough = [];

    tokens.forEach((token) => {
        const m = String(token).match(COMPACT_FWD_PAIR);
        if (m) {
            pairs.push([`${m[1]}${m[2]}`, `${m[3]}${m[4]}`]);
        } else {
            passthrough.push(token);
        }
    });

    if (pairs.length < 2) return tokens;

    const sortedFlat = sortForwardLegPairs(pairs, ascending);
    return [...compactPairChunksFromFlatLegs(sortedFlat), ...passthrough];
}

function sortCompactPairTokensAscending(tokens) {
    return sortCompactPairTokens(tokens, true);
}

const buildCombinedImmToken = (imm, endLeg) => {
    const immStem = String(imm?.label ?? '')
        .toUpperCase()
        .replace(/y$/i, '');
    return `${immStem}${normalizeLeg(endLeg)}`;
};

const immTokenSortKey = (token) => {
    const m = String(token).match(/^[MUZH](\d+)/i);
    return m ? parseInt(m[1], 10) : 0;
};

const isImmToken = (token) => /^[MUZH]\d/i.test(String(token ?? '').trim());

function sortImmDisplayTokens(label = '', ascending = true) {
    const tokens = String(label).trim().split(/\s+/).filter(Boolean);
    if (tokens.length < 2) return tokens.join(' ');
    if (!tokens.every(isImmToken)) return tokens.join(' ');
    return [...tokens]
        .sort((a, b) => {
            const diff = immTokenSortKey(a) - immTokenSortKey(b);
            return ascending ? diff : -diff;
        })
        .join(' ');
}

function sortImmLabelAsc(label = '') {
    return sortImmDisplayTokens(label, true);
}

const isImmCalendarYearLeg = (yearText) => {
    const yearNum = Number(yearText);
    if (!Number.isFinite(yearNum)) return false;
    if (String(yearText).includes('.')) return true;
    return yearNum >= 10;
};

const findImmByYear = (startLeg, validImm = []) => {
    if (!validImm?.length) return null;
    const yearText = String(startLeg ?? '').replace(/[ymdw]/i, '');
    if (!isImmCalendarYearLeg(yearText)) return null;
    const yearNum = Number(yearText);
    const byApprox = validImm.find((item) => {
        const iv = Number(item?.y);
        return !Number.isNaN(iv) && Math.abs(iv - yearNum) < 1e-4;
    });
    if (byApprox) return byApprox;
    return validImm.find((item) => String(item.y) === yearText) || null;
};

const immFromStartLabel = (startImmLabel) => {
    const label = String(startImmLabel ?? '').trim();
    if (!label) return null;
    return { label: label.toUpperCase() };
};

const findStartImmFromLeg = (startLeg, validImm, startImmLabel) => {
    const fromYear = findImmByYear(startLeg, validImm);
    if (fromYear) return fromYear;

    const fromLabel = findImmByLabel(startImmLabel, validImm);
    if (fromLabel) return fromLabel;

    return immFromStartLabel(startImmLabel);
};

function isImmFullTokenFwdPair(startLeg, endLeg, validImm) {
    const combined = `${startLeg}${endLeg}`;
    const match = combined.match(FULL_IMM_FWD_TOKEN);
    if (!match) return null;
    const imm = findImmByLabel(match[1], validImm);
    if (!imm) return null;
    const endNorm = normalizeLeg(`${match[2]}${match[3]}`);
    return buildCombinedImmToken(imm, endNorm);
}

function isImmSplitFwdLegPattern(legs, validImm = [], startImmLabel = null) {
    if (!legs?.length || legs.length < 4 || legs.length % 2 !== 0) return false;

    const startYears = [];
    for (let i = 0; i < legs.length; i += 2) {
        startYears.push(legs[i].replace(/[ymdw]/i, ''));
    }
    if (!startYears.every((y) => y === startYears[0])) return false;

    const sharedYear = startYears[0];
    const sharedYearNum = Number(sharedYear);
    const hasSharedImmYear = isImmCalendarYearLeg(sharedYear)
        && validImm.some((imm) => {
            const iv = Number(imm?.y);
            if (Number.isFinite(sharedYearNum) && Number.isFinite(iv)) {
                return Math.abs(iv - sharedYearNum) < 1e-4;
            }
            return String(imm.y) === sharedYear;
        });
    if (
        String(startImmLabel ?? '').trim()
        && startYears.every((y) => y === startYears[0])
    ) return true;
    if (!hasSharedImmYear && !findImmByLabel(startImmLabel, validImm)) return false;

    for (let i = 1; i < legs.length; i += 2) {
        const endYear = legs[i].replace(/[ymdw]/i, '');
        if (validImm.some((imm) => String(imm.y) === endYear)) return false;
    }

    return true;
}

function formatImmFullTokenFwdDisplayLabel(
    legs,
    validImm = [],
    startImmLabel = null
) {
    const kind = checkPlotStructure(
        normalizeDisplayLegsFromInput(legs).length,
        true
    );
    const normalized = sortFlatForwardLegs(legs, kind !== 'spread');
    if (!normalized.length) return '';

    if (normalized.length >= 2 && normalized.every(isImmLabelLeg)) {
        const immAscending = immLabelAsc(normalized.length, true);
        return [...normalized]
            .sort((a, b) => {
                const diff = immTokenSortKey(a) - immTokenSortKey(b);
                return immAscending ? diff : -diff;
            })
            .join(' ');
    }

    const labels = [];
    let immPairCount = 0;

    for (let i = 0; i < normalized.length; i += 2) {
        const endLeg = normalized[i + 1];
        if (!endLeg) break;

        const token = isImmFullTokenFwdPair(normalized[i], endLeg, validImm);
        if (token) {
            labels.push(token);
            immPairCount += 1;
            continue;
        }

        const startImm = findStartImmFromLeg(
            normalized[i],
            validImm,
            startImmLabel
        );
        if (startImm) {
            labels.push(buildCombinedImmToken(startImm, endLeg));
            immPairCount += 1;
        } else {
            labels.push(`${normalized[i]}${endLeg}`);
        }
    }

    if (!immPairCount) return '';

    const ascending = immPairCount !== 2;
    return sortImmDisplayTokens(labels.join(' '), ascending);
}

function formatImmSplitFwdDisplayLabel(
    legs,
    validImm = [],
    startImmLabel = null
) {
    const flatLegs = normalizeDisplayLegsFromInput(legs);
    const pairCount = flatLegs.length >= 2 && flatLegs.length % 2 === 0 ? flatLegs.length / 2 : 0;
    const normalized = sortFlatForwardLegs(legs, immLabelAsc(pairCount, true));
    if (normalized.length < 2) return '';

    const sharedStartImm = findImmByLabel(startImmLabel, validImm)
        || findImmByYear(normalized[0], validImm)
        || immFromStartLabel(startImmLabel);
    if (!sharedStartImm) return '';

    const labels = [];
    for (let i = 0; i < normalized.length; i += 2) {
        const endLeg = normalized[i + 1];
        if (!endLeg) break;
        labels.push(buildCombinedImmToken(sharedStartImm, endLeg));
    }

    return sortImmDisplayTokens(
        labels.join(' '),
        immLabelAsc(labels.length, true)
    );
}

function prepareDisplayLabeledLegs(legs, isImm, validImm) {
    const immByYear = isImm ? buildImmYearLabelMap(validImm) : null;

    return legs.map((leg, i) => {
        if (immByYear && i % 2 === 0) {
            const y = leg.replace(/[ymdw]/i, '');
            const immLabel = immByYear.get(y);
            if (immLabel) return immLabel;
        }
        return leg;
    });
}

function joinDisplayShortLabel(labeledLegs, isFwd, isImm) {
    if (!labeledLegs.length) return '';

    if (labeledLegs.length > 1 && labeledLegs.every(isImmLabelLeg)) {
        const immAscending = immLabelAsc(labeledLegs.length, isFwd);
        const joined = immAscending
            ? [...labeledLegs].sort((a, b) => immTokenSortKey(a) - immTokenSortKey(b))
            : [...labeledLegs].sort(
                (a, b) => immTokenSortKey(b) - immTokenSortKey(a)
            );
        return joined.join(' ').replace(ZERO_Y_SPOT, ' ').trim();
    }

    if (isFwd && (labeledLegs.length === 4 || labeledLegs.length === 6)) {
        const pairChunks = [];
        for (let i = 0; i < labeledLegs.length; i += 2) {
            pairChunks.push(labeledLegs[i] + labeledLegs[i + 1]);
        }
        return pairChunks.join(' ').replace(ZERO_Y_SPOT, ' ').trim();
    }

    const label = labeledLegs.join(
        isFwd && !isImm && !labeledLegs.every(isImmLabelLeg) ? '' : ' '
    );
    return label.replace(ZERO_Y_SPOT, ' ').trim();
}

function formatPlotStructureDisplayLabel({
    legs,
    isImm,
    isFwd,
    validImm = [],
    startImmLabel = null
}) {
    const normalized = sortDisplayLegs(legs, isFwd);
    if (!normalized.length) return '';

    if (normalized.length >= 2 && normalized.every(isImmLabelLeg)) {
        const immAscending = immLabelAsc(normalized.length, isFwd);
        const sorted = [...normalized].sort((a, b) => {
            const diff = immTokenSortKey(a) - immTokenSortKey(b);
            return immAscending ? diff : -diff;
        });
        return sorted.join(' ').replace(ZERO_Y_SPOT, ' ').trim();
    }

    if (isFwd && isImmSplitFwdLegPattern(normalized, validImm, startImmLabel)) {
        return formatImmSplitFwdDisplayLabel(normalized, validImm, startImmLabel)
            .replace(ZERO_Y_SPOT, ' ')
            .trim();
    }

    if (isFwd && normalized.length >= 2 && normalized.length % 2 === 0) {
        const immFwdLabel = formatImmFullTokenFwdDisplayLabel(
            normalized,
            validImm,
            startImmLabel
        );
        if (immFwdLabel) return immFwdLabel.replace(ZERO_Y_SPOT, ' ').trim();
    }

    const labeledLegs = prepareDisplayLabeledLegs(normalized, isImm, validImm);
    return joinDisplayShortLabel(labeledLegs, isFwd, isImm);
}

function tokenizeStructureKey(structureRaw = '') {
    return String(structureRaw)
        .trim()
        .toUpperCase()
        .replace(/,/g, ' ')
        .replace(/X/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean);
}

function legacyFwdChunksToShortLabel(tokens) {
    const pairLabels = [];
    const numericPairs = [];

    for (let i = 0; i < tokens.length; i++) {
        if (tokens[i + 1] !== 'FWD' || !tokens[i + 2]) continue;

        const start = tokens[i].replace(/\s+/g, '');
        let end = tokens[i + 2].replace(/\s+/g, '');
        let fwdSkip = 2;

        if (
            tokens[i + 3]
            && /^[DWMY]$/i.test(tokens[i + 3])
            && !/[DWMY]$/i.test(end)
        ) {
            end += tokens[i + 3];
            fwdSkip = 3;
        }

        if (/^[MUZH]\d+$/i.test(start)) {
            pairLabels.push(`${start}${end}`);
        } else {
            numericPairs.push([start, end]);
        }
        i += fwdSkip;
    }

    if (!pairLabels.length && !numericPairs.length) return '';

    const structureCount = pairLabels.length || numericPairs.length;
    const ascending = immLabelAsc(structureCount, true);
    const orderedImmLabels = pairLabels.length
        ? sortImmDisplayTokens(pairLabels.join(' '), ascending)
            .split(/\s+/)
            .filter(Boolean)
        : [];
    const numericLabels = numericPairs.length
        ? compactPairChunksFromFlatLegs(
            sortForwardLegPairs(numericPairs, ascending)
        )
        : [];

    return [...orderedImmLabels, ...numericLabels].join(' ');
}

function formatStructureSeriesKeyForDisplay(structureRaw = '') {
    const immTokens = extractImmLabel(structureRaw);
    if (immTokens.length > 1) {
        const isFwdKey = /\bFWD\b/i.test(structureRaw) || immTokens.every(isImmToken);
        return sortImmDisplayTokens(
            immTokens.join(' '),
            immLabelAsc(immTokens.length, isFwdKey)
        );
    }

    const tokens = tokenizeStructureKey(structureRaw);
    if (!tokens.length) return '';

    if (tokens.includes('FWD')) {
        const legacy = legacyFwdChunksToShortLabel(tokens);
        if (legacy) return legacy;
    }

    if (tokens.length > 1) {
        if (tokens.every((t) => STANDALONE_TENOR.test(t))) {
            const spotAscending = tokens.length !== 2;
            return sortSpotTenors(tokens, spotAscending).join(' ');
        }
        const compactPairs = tokens.filter((t) => COMPACT_FWD_PAIR.test(t));
        const compactAscending = compactPairs.length !== 2;
        return sortCompactPairTokens(tokens, compactAscending).join(' ');
    }

    if (tokens[0]) {
        const split = tokens[0].match(/\d+(?:\.\d+)?[DWMY]\d+(?:\.\d+)?[DWMY]/gi);
        if (split?.length > 1) {
            const compactAscending = split.length !== 2;
            return sortCompactPairTokens(split, compactAscending).join(' ');
        }
    }

    return tokens.join(' ');
}

function formatCurveSeriesDisplayLegendName(seriesName = '') {
    const [curve, structure] = String(seriesName).split('::');
    if (!structure) return curve || '';
    return `${curve} ${formatStructureSeriesKeyForDisplay(structure)}`;
}

function buildCurveStructureDisplaySeriesKey(
    curveName,
    legs,
    {
        isImm = false, isFwd = false, validImm = [], startImmLabel = null
    } = {}
) {
    const suffix = formatPlotStructureDisplayLabel({
        legs,
        isImm,
        isFwd,
        validImm,
        startImmLabel
    });
    if (!suffix || !curveName) return '';
    return `${curveName}::${suffix.toUpperCase()}`.replace(/\b0Y\b/gi, '');
}

function flattenStructureLegPieces(legsArr) {
    if (!Array.isArray(legsArr) || !legsArr.length) return [];
    return legsArr.flatMap((piece) => String(piece ?? '')
        .trim()
        .split(/[\s,]+/)
        .filter(Boolean));
}

function formatStructureInputDisplayLabel(
    legsArr,
    {
        command = '', depF = '', immFwd = null, validImm = []
    } = {}
) {
    const pieces = flattenStructureLegPieces(legsArr);
    if (!pieces.length) return '';

    const ctx = resolvePlotStructureDisplayContext({
        legCsv: pieces.join(','),
        command,
        depF,
        immFwd,
        validImm
    });

    const displayLabel = ctx.legs.length
        ? formatPlotStructureDisplayLabel({
            legs: ctx.legs,
            isImm: ctx.isImm,
            isFwd: ctx.isFwd && ctx.legs.length > 1,
            validImm,
            startImmLabel: ctx.startImmLabel
        })
        : '';

    if (!displayLabel) return '';
    const tokenCount = displayLabel.trim().split(/\s+/).filter(Boolean).length;
    const immAscending = immLabelAsc(tokenCount, ctx.isFwd);
    return sortImmDisplayTokens(displayLabel, immAscending).toLowerCase();
}

function resolveStructureDisplayValue(
    rawValue,
    {
        validImm = [],
        immFwd = null,
        command = 'BASIS',
        isIrsPage = true,
        allowFBfly = false
    } = {}
) {
    const trimmed = String(rawValue ?? '').trim();
    if (!trimmed) return trimmed;

    const structureRes = parsePlot(`plot ${trimmed}`, validImm, isIrsPage);
    if (
        !allowFBfly
        && [structureRes?.plotType].some((type) => type?.startsWith('fBfly'))
    ) return trimmed;
    if (!structureRes?.tenors?.length) return trimmed;

    const immHint = structureRes.imm?.label ?? structureRes.imm?.oldLabel ?? immFwd ?? null;

    const isFwdFlag = structureRes.isFwd || structureRes.imm;

    return (
        formatStructureInputDisplayLabel(structureRes.tenors, {
            command,
            depF: isFwdFlag ? '1' : '',
            immFwd: immHint,
            validImm
        }) || trimmed
    );
}

function resolvePlotStructureDisplayContext({
    legCsv = '',
    command = '',
    depF = '',
    immFwd = null,
    validImm = []
} = {}) {
    const rawLegs = normalizeDisplayLegsFromInput(legCsv);
    const isFwdCtx = (command === 'fwcm' || depF === '1') && rawLegs.length > 1;
    const legs = sortDisplayLegs(rawLegs, isFwdCtx);
    const isImm = !!immFwd;
    const isFwd = isFwdCtx;
    const startImmLabel = typeof immFwd === 'string' ? immFwd : immFwd?.label ?? null;

    return {
        legs,
        isImm,
        isFwd,
        validImm,
        startImmLabel,
        label: () => formatPlotStructureDisplayLabel({
            legs,
            isImm,
            isFwd,
            validImm,
            startImmLabel
        })
    };
}

const isSpreadStructure = (structureRaw = '', imm = []) => {
    const parse = parsePlot(`plot ${structureRaw}`, imm, true);
    const isOutright = parse?.tenors?.length === 1
        || (parse?.tenors?.length === 2 && parse?.isFwd);
    return !isOutright;
};

function appendBrokerTypeParam(url, brokerType) {
    if (!brokerType || !url) return url;
    const [base, queryString = ''] = String(url).split('?');
    const params = new URLSearchParams(queryString);
    params.set('brokerType', brokerType);
    return `${base}?${params.toString()}`;
}

function splitPathAndQuery(pathLike) {
    if (typeof pathLike !== 'string') return null;
    const [pathname, queryString = ''] = pathLike.split('?');
    const params = new URLSearchParams(queryString);
    return { pathname, params };
}

function isBrokerTypeOnlyPathDiff(prevPath, nextPath) {
    if (prevPath == null || nextPath == null) return false;
    if (prevPath === nextPath) return false;
    const prev = splitPathAndQuery(prevPath);
    const next = splitPathAndQuery(nextPath);
    if (!prev || !next) return false;
    if (prev.pathname !== next.pathname) return false;
    const prevKeys = [...prev.params.keys()]
        .filter((k) => k !== 'brokerType')
        .sort();
    const nextKeys = [...next.params.keys()]
        .filter((k) => k !== 'brokerType')
        .sort();
    if (prevKeys.length !== nextKeys.length) return false;
    for (let i = 0; i < prevKeys.length; i += 1) {
        const key = prevKeys[i];
        if (key !== nextKeys[i]) return false;
        if (prev.params.get(key) !== next.params.get(key)) return false;
    }
    return prev.params.get('brokerType') !== next.params.get('brokerType');
}

const replaceFwdStartLegsWithImm = (legArr, {
    validImm = [],
    immFwd = null,
    immLabel = null,
    immLabel3 = null
} = {}) => {
    if (!legArr) return legArr;
    if (typeof legArr === 'string') legArr = legArr.split(',');
    const tokens = legArr.map((s) => s.trim()).filter(Boolean);
    if (tokens.length < 2 || tokens.length % 2 !== 0) return legArr;

    let positionLabels;
    if (tokens.length === 2) positionLabels = [immFwd];
    else if (tokens.length === 4) positionLabels = [immLabel, immFwd];
    else if (tokens.length === 6) positionLabels = [immLabel3, immLabel, immFwd];
    else return legArr;

    let mutated = false;
    const resolved = tokens.map((token, idx) => {
        if (idx % 2 !== 0) return token;
        if (isImmLabelLeg(token) || isImmToken(token)) return token;
        const pairIdx = idx / 2;
        const queryLabel = positionLabels[pairIdx];
        if (queryLabel && String(queryLabel).trim()) {
            mutated = true;
            return String(queryLabel).toUpperCase();
        }
        const imm = findImmByYear(token, validImm);
        if (imm?.label) {
            mutated = true;
            return imm.label.toUpperCase();
        }
        return token;
    });

    return mutated ? resolved : legArr;
};

module.exports = {
    buildCurveStructureDisplaySeriesKey,
    flattenStructureLegPieces,
    formatCurveSeriesDisplayLegendName,
    formatPlotStructureDisplayLabel,
    formatStructureInputDisplayLabel,
    formatStructureSeriesKeyForDisplay,
    prepareDisplayLabeledLegs,
    replaceFwdStartLegsWithImm,
    replaceLegsWithCustomDates,
    resolvePlotStructureDisplayContext,
    resolveStructureDisplayValue,
    sortLegsAsc,
    sortFlatForwardLegsAscending,
    sortImmLabelAsc,
    sortSpotTenorsAscending,
    isSpreadStructure,
    appendBrokerTypeParam,
    isBrokerTypeOnlyPathDiff
};
